'use client';

import React, { useState, useRef } from 'react';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { sendGTMEvent } from '@/utils/analytics';

import { useConfirmModal } from '@/contexts/ConfirmModalContext';
import { useDrawer } from '@/contexts/DrawerContext';
import ConfirmModalContent from '@/components/layouts/ConfirmModalContent';
import Button from '@/components/common/Button';
import Textarea from '@/components/common/Textarea';
import EditorShortcuts from '@/components/common/EditorShortcuts';
import FormLabel from '@/components/common/FormLabel';
import FormErrorText from '@/components/common/FormErrorText';
import { GoalCreateDataModel } from '@/types/goals';

const MAX_LENGTH_IMEDIATE_GOAL = 150;

/**
 * props
 */
interface GoalFormProps {
  onFormSubmit: (formData: GoalCreateDataModel) => Promise<void>;
}

/**
 * yup - バリデーションを定義
 */
const schema = yup.object().shape({
  immediate_goal: yup
    .string()
    .required('この項目は必須です。')
    .matches(/^(?!\s*$).+/, 'この項目は必須です。')
    .max(MAX_LENGTH_IMEDIATE_GOAL, `${MAX_LENGTH_IMEDIATE_GOAL}文字以内で入力してください。`)
});

/**
 * 目標新規作成フォーム
 * @param param0
 * @returns
 */
const GoalForm: React.FC<GoalFormProps> = ({ onFormSubmit }) => {
  /**
   * React Context
   */
  // ドロワー
  const { closeDrawer } = useDrawer();
  const { openConfirmModal, closeConfirmModal } = useConfirmModal();

  /**
   * useRef
   */
  const textareaGoalRef = useRef<HTMLTextAreaElement | null>(null);

  /**
   * React Hook Form - のuseFormを使用してフォームを作成
   */
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    setFocus
  } = useForm<GoalCreateDataModel>({
    resolver: yupResolver(schema)
  });

  /**
   * React Hook Form register
   * FIXME: 編集ボタン付きテキストエリアで、ref参照つきで制御する場合、
   * 下記のように実装しないと機能しない。他と実装が揃っていないので他に良い実装があればリファクタしたい
   *
   * 経緯：
   * Controlでラップして実装するパターンだと、フォース制御が外れてしまい、スマホブラウザの挙動がおかしくなる
   * なのでControlを使用しないパターンでuseRefつきのフォームを作成しようとしたら以下のような実装になった。
   * https://www.react-hook-form.com/faqs/#Howtosharerefusage
   */
  const { ref: refGoal, ...restGoal } = register('immediate_goal');

  /**
   * React Hook Form watch
   */
  const immediateGoal = watch('immediate_goal', '');

  /**
   * useState
   */
  const [isLoading, setIsLoading] = useState(false);

  /**
   * デフォルトフォーカス
   * NOTE: ドロワーのフラグを監視して、表示されたらフォーカス
   */
  // useEffect(() => {
  //   setFocus('immediate_goal')
  // }, [isOpen])

  /**
   * 箇条書き挿入
   */
  const insertBulletPoint = (target_form_name: 'immediate_goal', ref: React.RefObject<HTMLTextAreaElement>) => {
    const textarea = ref.current;

    if (textarea) {
      let newValueWithBulletPoint = '';
      let space = 0;

      // カーソル位置取得
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      // 現在のValue値を取得
      const value = textarea.value;

      // 行単位で文字列分割
      const lines = value.split('\n');
      // 現在のカーソル位置の行数
      const currentLineIndex = value.slice(0, start).split('\n').length - 1;
      // 現在のカーソル位置の行の文字列
      const currentLine = lines[currentLineIndex] || '';
      // カーソル位置が行頭か
      const isAtLineStart = start === 0 || value.charAt(start - 1) === '\n';
      // カーソル位置が行末か
      const isAtLineEnd = end === value.length || value.charAt(end) === '\n';
      // カーソル位置が行途中か
      const isInTheMiddleOfLine = !isAtLineStart && !isAtLineEnd;

      // console.log(lines)
      // console.log(`currentLineIndex: ${currentLineIndex}`)
      // console.log(`currentLine: ${currentLine}`)
      // console.log(`isAtLineStart: ${isAtLineStart}`)
      // console.log(`isAtLineEnd: ${isAtLineEnd}`)
      // console.log(`isInTheMiddleOfLine: ${isInTheMiddleOfLine}`)

      // 行頭
      if (isAtLineStart) {
        //カーソル以降文字列なし
        if (currentLine === '') {
          newValueWithBulletPoint = value.substring(0, start) + '• ' + value.substring(end);
          space = 2;
        } else {
          newValueWithBulletPoint = value.substring(0, start) + '• ' + '\n' + value.substring(end);
          space = 2;
        }
        // 行途中
      } else if (isInTheMiddleOfLine) {
        //カーソル以降文字列なし
        if (currentLine === '') {
          newValueWithBulletPoint = value.substring(0, start) + '• ' + value.substring(end);
          space = 2;
        } else {
          newValueWithBulletPoint = value.substring(0, start) + '\n' + '• ' + value.substring(end);
          space = 3;
        }
        // 行末
      } else if (isAtLineEnd) {
        newValueWithBulletPoint = value.substring(0, start) + '\n' + '• ' + value.substring(end);
        space = 3;
      }

      // NOTE: 状態を更新した上でDOMの値を書き換えないと画面に反映されない
      setValue(target_form_name, newValueWithBulletPoint);
      textarea.value = newValueWithBulletPoint;

      // NOTE: 状態を更新した上でDOMの値を書き換えないと画面に反映されない
      setFocus(target_form_name);
      // NOTE:ReactがDOMを更新した後にカーソル位置を設定する必要がある。
      // setTimeoutはタスクキューに入れられるため、Reactのレンダリングサイクルが完了した後に実行される。
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + space;
        textarea.focus();
      }, 0);
    }
  };

  /**
   * 右矢印挿入
   */
  const insertArrow = (target_form_name: 'immediate_goal', ref: React.RefObject<HTMLTextAreaElement>) => {
    const textarea = ref.current;

    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const value = textarea.value;
      const newValue = value.substring(0, start) + '→ ' + value.substring(end);

      // NOTE: 状態を更新した上でDOMの値を書き換えないと画面に反映されない
      setValue(target_form_name, newValue, { shouldValidate: true });
      textarea.value = newValue;

      // NOTE: 状態を更新した上でDOMの値を書き換えないと画面に反映されない
      setFocus(target_form_name);
      // NOTE:ReactがDOMを更新した後にカーソル位置を設定する必要がある。
      // setTimeoutはタスクキューに入れられるため、Reactのレンダリングサイクルが完了した後に実行される。
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
        textarea.focus();
      }, 0);
    }
  };

  /**
   * フォームサブミット
   */
  const handleFormSubmit = async (formData: GoalCreateDataModel) => {
    // ボタンローディング表示
    setIsLoading(true);

    // console.log(formData);
    try {
      // GTM/GA イベント送信
      sendGTMEvent('createGoals');

      // サブミット処理（親コンポーネントでデータ更新）
      await onFormSubmit(formData);
      // ボタンローディング非表示
      setIsLoading(false);
    } catch {
      // ボタンローディング非表示
      setIsLoading(false);
    }
  };

  /**
   * 確認モーダルOK時の処理
   */
  const handleConfirmCloseOK = () => {
    // 確認モーダル閉じる（このタイミングがUX的に重要）
    closeConfirmModal();
    closeDrawer();
  };

  /**
   * 確認モーダル表示
   */
  const handleConfirmClose = () => {
    openConfirmModal(
      <ConfirmModalContent onClickOKCallback={handleConfirmCloseOK}>
        新規作成を終了します。
        <br />
        よろしいですか？
      </ConfirmModalContent>
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="pt-4 px-4 pb-8">
          <FormLabel sub="（目標・課題）">My Goal</FormLabel>
          <div className="border border-gray-300 focus-within:ring-blue-500 focus-within:ring-1 focus-within:border-blue-500 rounded-lg bg-gray-50 p-1">
            <div className="flex justify-between items-center bg-gray-100 rounded p-2 shadow mb-1">
              <div></div>
              <EditorShortcuts
                onInsertArrow={() => insertArrow('immediate_goal', textareaGoalRef)}
                onInsertBulletPoint={() => insertBulletPoint('immediate_goal', textareaGoalRef)}
              />
            </div>
            <Textarea
              id="immediate_goal"
              rows={6}
              className="text-base w-full border-0 focus:ring-0"
              placeholder="トレーニングやゲームで意識すること、克服したい課題を掲げよう"
              {...restGoal}
              ref={(e) => {
                refGoal(e);
                textareaGoalRef.current = e;
              }}>
              {MAX_LENGTH_IMEDIATE_GOAL - (immediateGoal?.length || 0) <= 20 && (
                <div
                  className={`text-xs font-semibold ${MAX_LENGTH_IMEDIATE_GOAL - (immediateGoal?.length || 0) < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                  残り{MAX_LENGTH_IMEDIATE_GOAL - (immediateGoal?.length || 0)}文字
                </div>
              )}
            </Textarea>
          </div>
          {errors.immediate_goal && <FormErrorText>{errors.immediate_goal.message}</FormErrorText>}
        </div>
        <div className="flex items-center justify-end border-t border-green-500 p-4">
          <Button className="me-2" type="button" buttonType="outlinePrimary" onClick={handleConfirmClose}>
            キャンセル
          </Button>
          <Button type="submit" isLoading={isLoading}>
            OK
          </Button>
        </div>
      </form>
    </>
  );
};

export default GoalForm;
